/* eslint-disable no-console */
/* eslint-disable import/no-cycle */
import jwtDecode from "jwt-decode";

import { getRoleNameProfileSA } from "../api/MasterUser";
import { HttpService } from "../constants/HTTPService";
import { IJwtTokenData } from "../interfaces/IJwtTokenData";
import {
  setNewProfile,
  setNewProfileAvatar,
  setNewProfileRole,
} from "../store/newProfile/slice";
import { store } from "../store/store";
import { TokenStatus } from "../types/token";
import { fetchAndDisplayProfileImage, GetGraphData } from "./msGraph";
import { getMicrosoftTokenSilent } from "./new-azure-ad";

const AUTH_TOKEN_KEY = "app_auth_token";
const LAST_PAGE_KEY = "app_last_page";
const IS_BYPASS_LOGIN_KEY = "app_bypass_login";
const TOKEN_REFRESH_COOLDOWN = 2000;
const LOCK_CLEAR_DELAY = 500;

let tokenRefreshPromise: Promise<string | null> | null = null;
let lastRefreshTimestamp = 0;

export function getAzureADTokenPayload(token: string) {
  return jwtDecode(token);
}

export function setAuthToken(token: string): void {
  localStorage.setItem(AUTH_TOKEN_KEY, token);
}

export function getAuthToken(): TokenStatus | null {
  const token = localStorage.getItem(AUTH_TOKEN_KEY);
  if (!token) {
    return null;
  }

  try {
    const decodedToken: IJwtTokenData = jwtDecode(token);
    const now = Math.floor(Date.now() / 1000);
    const isExpired = now >= decodedToken.exp;
    return { value: token, isExpired };
  } catch {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    return null;
  }
}

export function removeAuthToken(): void {
  localStorage.removeItem(AUTH_TOKEN_KEY);
}

export function setLastVisitedPage(url: string): void {
  sessionStorage.setItem(LAST_PAGE_KEY, url);
}

export function getLastVisitedPage(): null | string {
  return sessionStorage.getItem(LAST_PAGE_KEY);
}

export function removeLastVisitedPage(): void {
  sessionStorage.removeItem(LAST_PAGE_KEY);
}

export function setIsBypassLogin(isBypass: boolean = false): void {
  localStorage.setItem(IS_BYPASS_LOGIN_KEY, JSON.stringify(isBypass));
}

export function getIsBypassLogin(): boolean {
  const isBypass = localStorage.getItem(IS_BYPASS_LOGIN_KEY);
  return isBypass ? JSON.parse(isBypass) : false;
}

export function removeIsBypassLogin(): void {
  localStorage.removeItem(IS_BYPASS_LOGIN_KEY);
}

export async function refreshToken(): Promise<string | null> {
  const now = Date.now();
  if (now - lastRefreshTimestamp < TOKEN_REFRESH_COOLDOWN) {
    const currentToken = getAuthToken();
    if (currentToken?.value && !currentToken.isExpired) {
      console.log("[Token] Using recently refreshed token");
      return currentToken.value;
    }
  }

  if (tokenRefreshPromise) {
    console.log("[Token] Refresh in progress, reusing existing promise");
    return tokenRefreshPromise;
  }

  console.log("[Token] Starting new token refresh");
  tokenRefreshPromise = (async () => {
    try {
      const msalAccessToken = await getMicrosoftTokenSilent();
      if (!msalAccessToken) {
        console.log("[Token] Failed to get MSAL token");
        removeAuthToken();
        return null;
      }

      const userData = await GetGraphData(msalAccessToken);
      if (!userData.mail.includes("@binus.edu")) {
        console.log("[Token] User email not from binus.edu domain");
        removeAuthToken();
        return null;
      }

      const authResponse = await HttpService.getAuthorizationTokenMsal(
        msalAccessToken
      );
      const { tokenData } = authResponse.data;
      const decodedToken: IJwtTokenData = jwtDecode(tokenData.token);

      setAuthToken(tokenData.token);
      lastRefreshTimestamp = Date.now();

      const [profileImageBase64, roleProfile] = await Promise.all([
        fetchAndDisplayProfileImage(msalAccessToken),
        getRoleNameProfileSA(decodedToken.BinusianId),
      ]);

      store.dispatch(setNewProfile(decodedToken));
      store.dispatch(setNewProfileRole(roleProfile));
      store.dispatch(setNewProfileAvatar(profileImageBase64));

      return tokenData.token;
    } catch (error) {
      console.error("[Token] Failed to refresh token:", error);
      removeAuthToken();
      return null;
    } finally {
      setTimeout(() => {
        tokenRefreshPromise = null;
      }, LOCK_CLEAR_DELAY);
    }
  })();

  return tokenRefreshPromise;
}
